import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";
import { Loader2, Euro, Users, AlertCircle, Building2, User, Globe, MapPin, Package, DoorOpen, TrendingUp, CalendarDays } from "lucide-react";
import "leaflet/dist/leaflet.css";
import { api } from "../lib/api";

const fmtEUR = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0, useGrouping: "always" }).format(n || 0);
const GOLD = "#b8933f";
const INK = "#2b2a28";
const COLORS = ["#b8933f", "#2b2a28", "#8a7a52", "#c6b48a", "#5c5649", "#9c7b3a"];

export default function StoreInsightStats() {
  const [data, setData] = useState(null);
  const [sr, setSr] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const { data } = await api.get("/admin/store-insight/stats"); setData(data); }
    catch { /* */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/admin/store-insight/showroom-stats").then(({ data }) => setSr(data)).catch(() => {}); }, []);
  // se il geocoding è in corso, ricarica dopo un po' per popolare la mappa
  useEffect(() => {
    if (data?.geocoding_pending > 0) {
      const t = setTimeout(load, 15000);
      return () => clearTimeout(t);
    }
  }, [data]);

  if (loading) return <div className="py-20 text-center text-slate1"><Loader2 className="animate-spin inline" /> Calcolo statistiche…</div>;
  if (!data) return <div className="py-20 text-center text-slate1">Nessun dato. Esegui prima una sincronizzazione.</div>;

  const t = data.totals;
  const typeData = [
    { name: "Ditte", value: data.by_type.ditta?.clients || 0 },
    { name: "Privati", value: data.by_type.privato?.clients || 0 },
  ];
  const expData = [
    { name: "Italia", value: data.export.italia?.revenue || 0 },
    { name: "Export", value: data.export.export?.revenue || 0 },
  ];
  const provData = (data.by_province || []).filter((p) => p.province !== "—").slice(0, 10).map((p) => ({ name: p.province, Fatturato: p.revenue }));
  const prodData = (data.by_category || []).slice(0, 10).map((p) => ({ name: p.name, Valore: p.amount }));

  return (
    <div className="space-y-6" data-testid="si-stats">
      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SKpi icon={<Users size={16} />} label="Clienti" value={t.clients} />
        <SKpi icon={<Euro size={16} />} label="Fatturato 3 anni" value={fmtEUR(t.revenue)} />
        <SKpi icon={<Euro size={16} />} label="Scontrino medio" value={fmtEUR(t.avg_revenue)} />
        <SKpi icon={<AlertCircle size={16} />} label="Insoluti" value={fmtEUR(t.outstanding)} highlight={t.outstanding > 0}
              sub={t.last_sync ? `aggiornato al ${new Date(t.last_sync).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}` : "mai sincronizzato — premi Sincronizza"} />
      </div>

      {/* ---- Flussi di ingresso Show Room ---- */}
      <ShowroomFlows sr={sr} />

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Ditte vs privati */}
        <Panel title="Ditte vs Privati" icon={<Building2 size={14} />}>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={typeData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label>
                {typeData.map((e, i) => <Cell key={i} fill={COLORS[i]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-5 text-xs text-slate1">
            <span className="inline-flex items-center gap-1"><Building2 size={12} /> Ditte: <b className="text-ink">{data.by_type.ditta?.clients || 0}</b></span>
            <span className="inline-flex items-center gap-1"><User size={12} /> Privati: <b className="text-ink">{data.by_type.privato?.clients || 0}</b></span>
          </div>
        </Panel>

        {/* Export vs Italia */}
        <Panel title="Mercato: Italia vs Export (IVA 0%)" icon={<Globe size={14} />}>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={expData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={({ name }) => name}>
                {expData.map((e, i) => <Cell key={i} fill={COLORS[i]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtEUR(v)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-5 text-xs text-slate1">
            <span>Italia: <b className="text-ink">{fmtEUR(data.export.italia?.revenue)}</b></span>
            <span>Export: <b className="text-ink">{fmtEUR(data.export.export?.revenue)}</b></span>
          </div>
        </Panel>
      </div>

      {/* Mappa distribuzione */}
      <Panel title="Distribuzione sul territorio" icon={<MapPin size={14} />}
        extra={data.geocoding_pending > 0 ? <span className="text-[11px] text-slate1 inline-flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> localizzo {data.geocoding_pending} città…</span> : null}>
        <div className="relative z-0 isolate rounded-sm overflow-hidden border border-ink/10" style={{ height: 380 }} data-testid="si-stats-map">
          <MapContainer center={[42.5, 12.5]} zoom={5} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <FitBounds points={data.geo_points || []} />
            <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {(data.geo_points || []).map((p, i) => (
              <CircleMarker key={i} center={[p.lat, p.lon]} radius={Math.min(8 + p.clients * 2, 26)}
                pathOptions={{ color: GOLD, fillColor: GOLD, fillOpacity: 0.5, weight: 1 }}>
                <Popup>
                  <b>{p.city}</b>{p.province ? ` (${p.province})` : ""}<br />
                  {p.clients} clienti · {fmtEUR(p.revenue)}
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
        {(data.geo_points || []).length === 0 && (
          <div className="text-xs text-slate1 mt-2">Nessuna città localizzata ancora. Serve l'indirizzo cliente da Fatture in Cloud; la mappa si popola automaticamente dopo la sincronizzazione.</div>
        )}
      </Panel>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Fatturato per provincia / paese */}
        {provData.length > 0 && (
          <Panel title="Fatturato per provincia / paese" icon={<MapPin size={14} />}>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={provData} layout="vertical" margin={{ left: 10 }}>
                <XAxis type="number" tickFormatter={(v) => `${Math.round(v / 1000)}k`} fontSize={11} />
                <YAxis type="category" dataKey="name" width={110} fontSize={11} interval={0} />
                <Tooltip formatter={(v) => fmtEUR(v)} />
                <Bar dataKey="Fatturato" fill={GOLD} radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>
        )}

        {/* Prodotti più venduti */}
        <Panel title="Categorie prodotti più acquistate" icon={<Package size={14} />}>
          {prodData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={prodData} layout="vertical" margin={{ left: 10 }}>
                <XAxis type="number" tickFormatter={(v) => `${Math.round(v / 1000)}k`} fontSize={11} />
                <YAxis type="category" dataKey="name" width={130} fontSize={11} interval={0} />
                <Tooltip formatter={(v) => fmtEUR(v)} />
                <Bar dataKey="Valore" fill={INK} radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex flex-col items-center justify-center text-center gap-2 text-slate1" data-testid="si-cat-empty">
              <Package size={28} className="opacity-40" />
              <p className="text-sm">Ancora nessuna categoria di prodotto calcolata.</p>
              <p className="text-xs max-w-xs">Premi <b>Ricategorizza</b> per calcolare subito le macro-categorie dai dati già sincronizzati, oppure <b>Sincronizza</b> per la riclassificazione AI completa dalle righe fattura.</p>
            </div>
          )}
        </Panel>
      </div>

      {/* Top clienti */}
      <Panel title="Top clienti per fatturato" icon={<Euro size={14} />}>
        <div className="divide-y divide-ink/5">
          {data.top_clients.map((c, i) => (
            <div key={i} className="flex items-center justify-between py-2 text-sm" data-testid={`si-top-client-${i}`}>
              <span className="text-ink truncate">{i + 1}. {c.name}</span>
              <span className="flex items-center gap-4 shrink-0">
                {c.outstanding > 0 && <span className="text-[11px] text-rose-600">insoluto {fmtEUR(c.outstanding)}</span>}
                <span className="text-gold tabular-nums font-medium">{fmtEUR(c.revenue)}</span>
              </span>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

function ShowroomFlows({ sr }) {
  const [selWeek, setSelWeek] = useState("");
  if (!sr) return null;
  const weekData = (sr.by_week || []).map((w) => ({ name: (w.label || "").replace(/^\d{4}-/, ""), Accessi: w.count }));
  const monthData = (sr.by_month || []).map((m) => ({ name: m.label, Accessi: m.count }));
  const typeData = (sr.by_request_type || []).slice(0, 8);
  const wdData = (sr.by_weekday || []).map((d) => ({ name: d.name, Accessi: d.count }));
  const mc = sr.month_compare;
  const wdDetail = sr.week_day_detail || [];
  const curWeek = selWeek || (wdDetail.length ? wdDetail[wdDetail.length - 1].week : "");
  const curWeekObj = wdDetail.find((w) => w.week === curWeek);
  const weekDayData = (curWeekObj?.days || []).map((d) => ({ name: d.name, Accessi: d.count }));
  if (!sr.total) {
    return (
      <Panel title="Flussi di ingresso Show Room" icon={<DoorOpen size={14} />}>
        <div className="text-sm text-slate1 py-6 text-center" data-testid="si-sr-flows-empty">
          Nessun accesso allo show room registrato. Le note vocali senza nominativo cliente alimentano questa analisi.
        </div>
      </Panel>
    );
  }
  return (
    <div data-testid="si-sr-flows">
      <div className="text-[11px] tracking-[0.18em] uppercase text-slate1 inline-flex items-center gap-2 mb-2"><DoorOpen size={14} /> Accesso Show Room · {sr.total} accessi totali</div>
      {mc && (
        <div className="grid grid-cols-3 gap-3 mb-4" data-testid="si-month-compare">
          <div className="rounded-sm border border-ink/10 bg-white p-3">
            <div className="text-[10px] tracking-[0.15em] uppercase text-slate1">{mc.current.label}</div>
            <div className="text-2xl font-semibold text-ink mt-1 tabular-nums">{mc.current.count}</div>
            <div className="text-[10px] text-slate1">accessi questo mese</div>
          </div>
          <div className="rounded-sm border border-ink/10 bg-cream2/40 p-3">
            <div className="text-[10px] tracking-[0.15em] uppercase text-slate1">{mc.previous_year.label}</div>
            <div className="text-2xl font-semibold text-ink/70 mt-1 tabular-nums">{mc.previous_year.count}</div>
            <div className="text-[10px] text-slate1">stesso mese anno prec.</div>
          </div>
          <div className={`rounded-sm border p-3 ${mc.delta >= 0 ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`}>
            <div className="text-[10px] tracking-[0.15em] uppercase text-slate1">Variazione</div>
            <div className={`text-2xl font-semibold mt-1 tabular-nums ${mc.delta >= 0 ? "text-emerald-700" : "text-rose-600"}`}>
              {mc.delta >= 0 ? "+" : ""}{mc.delta}
            </div>
            <div className="text-[10px] text-slate1">{mc.delta_pct === null ? "nessun dato anno prec." : `${mc.delta_pct >= 0 ? "+" : ""}${mc.delta_pct}% sull'anno prec.`}</div>
          </div>
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Flusso ingressi per settimana" icon={<TrendingUp size={14} />}>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={weekData} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="name" fontSize={11} />
              <YAxis allowDecimals={false} fontSize={11} />
              <Tooltip />
              <Line type="monotone" dataKey="Accessi" stroke={GOLD} strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Accessi per mese" icon={<CalendarDays size={14} />}>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={monthData} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="name" fontSize={11} />
              <YAxis allowDecimals={false} fontSize={11} />
              <Tooltip />
              <Bar dataKey="Accessi" fill={INK} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>
      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <Panel title="Categorie di richieste" icon={<Package size={14} />}>
          {typeData.length === 0 ? <div className="text-xs text-slate1 py-6 text-center">Nessuna categoria rilevata.</div> : (
            <ResponsiveContainer width="100%" height={Math.max(200, typeData.length * 34)}>
              <BarChart data={typeData} layout="vertical" margin={{ left: 10 }}>
                <XAxis type="number" allowDecimals={false} fontSize={11} />
                <YAxis type="category" dataKey="name" width={120} fontSize={11} />
                <Tooltip />
                <Bar dataKey="count" name="Richieste" fill={GOLD} radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>
        <Panel title="Affluenza per giorno della settimana" icon={<CalendarDays size={14} />}>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={wdData} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="name" fontSize={11} />
              <YAxis allowDecimals={false} fontSize={11} />
              <Tooltip />
              <Bar dataKey="Accessi" fill="#8a7a52" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>
      {wdDetail.length > 0 && (
        <div className="mt-6">
          <Panel
            title="Accessi per giorno — per settimana"
            icon={<CalendarDays size={14} />}
            extra={
              <select value={curWeek} onChange={(e) => setSelWeek(e.target.value)} className="text-xs border border-ink/20 rounded-sm px-2 py-1 bg-white" data-testid="si-week-select">
                {wdDetail.slice().reverse().map((w) => (
                  <option key={w.week} value={w.week}>{w.week.replace(/^\d{4}-/, "")} · {w.total} accessi</option>
                ))}
              </select>
            }
          >
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={weekDayData} margin={{ left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="name" fontSize={11} />
                <YAxis allowDecimals={false} fontSize={11} />
                <Tooltip />
                <Bar dataKey="Accessi" fill={GOLD} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>
        </div>
      )}
    </div>
  );
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) { map.setView([points[0].lat, points[0].lon], 9); }
    else if (points.length > 1) {
      map.fitBounds(points.map((p) => [p.lat, p.lon]), { padding: [40, 40], maxZoom: 11 });
    }
  }, [points, map]);
  return null;
}

function SKpi({ icon, label, value, highlight, sub }) {
  return (
    <div className={`rounded-sm border p-3 ${highlight ? "border-rose-300 bg-rose-50" : "border-ink/10 bg-white"}`}>
      <div className="text-[10px] tracking-[0.15em] uppercase text-slate1 inline-flex items-center gap-1.5">{icon} {label}</div>
      <div className="text-xl font-semibold text-ink mt-1 tabular-nums">{value}</div>
      {sub && <div className="text-[10px] text-slate1 mt-1 leading-tight">{sub}</div>}
    </div>
  );
}

function Panel({ title, icon, extra, children }) {
  return (
    <div className="border border-ink/10 bg-white rounded-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] tracking-[0.18em] uppercase text-slate1 inline-flex items-center gap-2">{icon} {title}</div>
        {extra}
      </div>
      {children}
    </div>
  );
}
